import { useMemo, useRef } from "react";
import type {
  Catalog,
  ConfiguratorConfig,
  RendererKind,
} from "@/configurator/domain/models/types";
import { Configurator } from "@/configurator/presentation/Configurator";
import { useConfiguratorDomain } from "@/configurator/domain/hooks/useConfiguratorDomain";
import {
  getConfigById,
  getStartupConfig,
} from "@/configurator/data/api/configs";

type Props = {
  config?: ConfiguratorConfig;
  configId?: string;
  renderer?: RendererKind;
  assetsBaseUrl?: string;
};

function resolveAssetUrl(
  url: string | undefined,
  baseUrl: string | undefined,
): string | undefined {
  if (!url || !baseUrl) return url;
  if (
    /^(https?:)?\/\//i.test(url) ||
    url.startsWith("data:") ||
    url.startsWith("blob:")
  ) {
    return url;
  }
  try {
    return new URL(url, baseUrl).toString();
  } catch {
    return url;
  }
}

function resolveCatalogAssets(
  catalog: Catalog,
  assetsBaseUrl: string | undefined,
): Catalog {
  if (!assetsBaseUrl) return catalog;

  return {
    ...catalog,
    products: catalog.products.map((product) => ({
      ...product,
      optionGroups: product.optionGroups.map((group) => ({
        ...group,
        options: group.options.map((option) => ({
          ...option,
          // Offer-PDF artwork, on every option kind.
          image: resolveAssetUrl(option.image, assetsBaseUrl),
          ...("texture" in option
            ? {
                texture: resolveAssetUrl(option.texture, assetsBaseUrl),
                normalMap: resolveAssetUrl(option.normalMap, assetsBaseUrl),
                bumpMap: resolveAssetUrl(option.bumpMap, assetsBaseUrl),
                reflexMap: resolveAssetUrl(option.reflexMap, assetsBaseUrl),
              }
            : {}),
        })),
      })) as typeof product.optionGroups,
    })),
  };
}

function resolveConfigAssets(
  config: ConfiguratorConfig,
  assetsBaseUrl: string | undefined,
): ConfiguratorConfig {
  if (!assetsBaseUrl) return config;

  return {
    ...config,
    brand: {
      ...config.brand,
      logoSrc: resolveAssetUrl(config.brand.logoSrc, assetsBaseUrl),
      pdfLogoSrc: resolveAssetUrl(config.brand.pdfLogoSrc, assetsBaseUrl),
    },
    model: {
      ...config.model,
      options: config.model.options.map((option) => ({
        ...option,
        path: resolveAssetUrl(option.path, assetsBaseUrl) ?? option.path,
      })),
    },
    catalog: resolveCatalogAssets(config.catalog, assetsBaseUrl),
  };
}

export function WidgetRoot(props: Props) {
  const { applyConfig } = useConfiguratorDomain();
  const config = useMemo(() => {
    const defaultConfig =
      getConfigById(props.configId) ?? getStartupConfig("widget");
    const baseConfig = props.config ?? defaultConfig;

    return resolveConfigAssets(baseConfig, props.assetsBaseUrl);
  }, [props.config, props.configId, props.assetsBaseUrl]);

  // Apply the resolved config to the store during render, before the child
  // renderers mount. Child effects run before parent effects, so doing this in a
  // useEffect would let the 3D scene mount against the initial demo config and
  // request an unresolved model path (404 on third-party origins). The ref guard
  // keeps this idempotent and avoids an update loop.
  const appliedConfigRef = useRef<ConfiguratorConfig | null>(null);
  if (appliedConfigRef.current !== config) {
    appliedConfigRef.current = config;
    applyConfig(config);
  }

  return <Configurator renderer={props.renderer} />;
}
